/**
 * Draw a SceneElement into a 2D canvas context.
 *
 * Caller contract:
 *   - `ctx` is already in *map space* (the Viewport transform has been
 *     applied). Drawing uses raw element coordinates — no conversion here.
 *   - `ctx` has been save()'d; this function does not save/restore.
 *     That lets the renderer loop save once before the whole batch.
 *
 * We deliberately do not depend on roughjs here — the vibe we want for a
 * VTT is "neat ink on parchment", not "sketch-style wobble". If a player
 * wants sketchy strokes later we can branch on element.roughness > 0 and
 * pull roughjs in as an option.
 *
 * Freehand strokes use `perfect-freehand` to convert point+pressure samples
 * into a smooth outline polygon, which we fill as a solid shape. That gives
 * the variable-width, tapered look you'd expect from a real pen.
 */

import { getStroke } from "perfect-freehand";
import type { SceneElement } from "../../lib/scene";
import { imageCache } from "./imageCache";

// Dash patterns, values are in map units.
const DASH: Record<string, number[]> = {
  solid: [],
  dashed: [8, 6],
  dotted: [2, 4],
};

export function drawElement(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  if (el.isDeleted) return;

  ctx.globalAlpha = el.opacity;
  ctx.strokeStyle = el.strokeColor;
  ctx.lineWidth = el.strokeWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.setLineDash(DASH[el.strokeStyle] ?? []);

  switch (el.type) {
    case "freedraw":
      drawFreehand(ctx, el);
      break;
    case "rect":
      drawRect(ctx, el);
      break;
    case "ellipse":
      drawEllipse(ctx, el);
      break;
    case "line":
      drawLine(ctx, el);
      break;
    case "arrow":
      drawArrow(ctx, el);
      break;
    case "text":
      drawText(ctx, el);
      break;
    case "image":
      drawImage(ctx, el);
      break;
  }

  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------

function drawFreehand(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  const points = el.points;
  if (!points || points.length < 2) return;

  // perfect-freehand expects [{x,y,pressure}, ...] relative to origin.
  // Our element stores a flat number array offset from (el.x, el.y).
  const pressures = el.pressures;
  const samples: number[][] = [];
  for (let i = 0; i < points.length; i += 2) {
    const p = pressures ? pressures[i / 2] : 0.5;
    samples.push([el.x + points[i], el.y + points[i + 1], p]);
  }

  const outline = getStroke(samples, {
    size: el.strokeWidth * 4, // size is "thickness" — freehand looks thin at 1:1
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    last: true,
  });
  if (outline.length === 0) return;

  ctx.fillStyle = el.strokeColor;
  ctx.beginPath();
  ctx.moveTo(outline[0][0], outline[0][1]);
  for (let i = 1; i < outline.length; i++) {
    ctx.lineTo(outline[i][0], outline[i][1]);
  }
  ctx.closePath();
  ctx.fill();
}

function drawRect(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  if (el.backgroundColor && el.fillStyle !== "none") {
    ctx.fillStyle = el.backgroundColor;
    ctx.fillRect(el.x, el.y, el.width, el.height);
  }
  ctx.strokeRect(el.x, el.y, el.width, el.height);
}

function drawEllipse(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const rx = Math.abs(el.width / 2);
  const ry = Math.abs(el.height / 2);
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  if (el.backgroundColor && el.fillStyle !== "none") {
    ctx.fillStyle = el.backgroundColor;
    ctx.fill();
  }
  ctx.stroke();
}

function drawLine(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  const pts = el.points;
  if (!pts || pts.length < 4) return;
  ctx.beginPath();
  ctx.moveTo(el.x + pts[0], el.y + pts[1]);
  for (let i = 2; i < pts.length; i += 2) {
    ctx.lineTo(el.x + pts[i], el.y + pts[i + 1]);
  }
  ctx.stroke();
}

function drawArrow(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  const pts = el.points;
  if (!pts || pts.length < 4) return;

  // Body
  ctx.beginPath();
  ctx.moveTo(el.x + pts[0], el.y + pts[1]);
  for (let i = 2; i < pts.length; i += 2) {
    ctx.lineTo(el.x + pts[i], el.y + pts[i + 1]);
  }
  ctx.stroke();

  // Arrow head on the last segment.
  const n = pts.length;
  const x1 = el.x + pts[n - 4];
  const y1 = el.y + pts[n - 3];
  const x2 = el.x + pts[n - 2];
  const y2 = el.y + pts[n - 1];
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const headLen = Math.max(8, el.strokeWidth * 4);
  const headAngle = Math.PI / 6;

  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLen * Math.cos(angle - headAngle),
    y2 - headLen * Math.sin(angle - headAngle),
  );
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLen * Math.cos(angle + headAngle),
    y2 - headLen * Math.sin(angle + headAngle),
  );
  ctx.stroke();
}

function drawText(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  if (!el.text) return;
  const size = el.fontSize ?? 16;
  ctx.fillStyle = el.strokeColor;
  ctx.font = `${size}px 'Crimson Pro', 'Georgia', serif`;
  ctx.textBaseline = "top";
  // Support multi-line text with explicit \n — line height 1.2.
  const lines = el.text.split("\n");
  const lh = size * 1.2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], el.x, el.y + i * lh);
  }
}

function drawImage(ctx: CanvasRenderingContext2D, el: SceneElement): void {
  // No fileId means the element was created without a reference — shouldn't
  // normally happen, but draw an empty placeholder rather than throwing so
  // a bad piece of wire data never crashes the canvas.
  if (!el.fileId) {
    drawImagePlaceholder(ctx, el, "(no file)");
    return;
  }

  const img = imageCache.get(el.fileId);
  if (!img) {
    // Still loading (or waiting on a URL provider). The cache will notify
    // listeners when the decode finishes, and the canvas schedules a
    // repaint from that notification — no polling needed here.
    drawImagePlaceholder(ctx, el, "loading…");
    return;
  }

  // Fit the image into the element box with aspect preserved (contain),
  // centred. The element box is what the size picker chose; the underlying
  // image keeps its natural proportions.
  const nw = img.naturalWidth || el.naturalWidth || el.width;
  const nh = img.naturalHeight || el.naturalHeight || el.height;
  const boxW = Math.abs(el.width);
  const boxH = Math.abs(el.height);
  if (boxW <= 0 || boxH <= 0 || nw <= 0 || nh <= 0) return;

  const scale = Math.min(boxW / nw, boxH / nh);
  const drawW = nw * scale;
  const drawH = nh * scale;
  const dx = el.x + (boxW - drawW) / 2;
  const dy = el.y + (boxH - drawH) / 2;
  ctx.drawImage(img, dx, dy, drawW, drawH);
}

function drawImagePlaceholder(ctx: CanvasRenderingContext2D, el: SceneElement, label = "(image)"): void {
  ctx.save();
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = "rgba(255,255,255,0.3)";
  ctx.strokeRect(el.x, el.y, el.width, el.height);
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.font = "12px 'JetBrains Mono', monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, el.x + el.width / 2, el.y + el.height / 2);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Selection overlay — drawn separately so it sits above the element fill.
// ---------------------------------------------------------------------------

export function drawSelectionBox(
  ctx: CanvasRenderingContext2D,
  el: SceneElement,
  mapToScreenScale: number,
): void {
  // The caller hands us the current zoom so we can keep the outline 1px
  // thick on screen regardless of how far the user has zoomed.
  const pad = 4 / mapToScreenScale;
  const lw = 1.5 / mapToScreenScale;
  ctx.save();
  ctx.strokeStyle = "#6ac1c4"; // teal — matches --sheet-b
  ctx.lineWidth = lw;
  ctx.setLineDash([6 / mapToScreenScale, 4 / mapToScreenScale]);
  ctx.strokeRect(el.x - pad, el.y - pad, el.width + pad * 2, el.height + pad * 2);
  ctx.restore();
}
